import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Facebook, Instagram, Play, RadioTower, RefreshCw, Square, Youtube } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { toast } from "../../components/ui/sonner";
import { useStudioAuth } from "../StudioAuthContext";
import { FieldLabel, OPS_INPUT, Panel, SkeletonRows, StatusChip } from "../ui";

const TikTokIcon = () => <span className="mono text-[11px] font-black">TT</span>;
const icons = { youtube: Youtube, facebook: Facebook, instagram: Instagram, tiktok: TikTokIcon };
const modes = [
  { key: "url", label: "Source URL", hint: "Relay an existing HLS / RTMP / SRT stream" },
  { key: "push_in", label: "Push into Storm Desk", hint: "OBS pushes to us (needs an exposed RTMP host)" },
  { key: "server_render", label: "Server-rendered radar", hint: "Storm Desk renders the radar itself (needs a media host)" },
];

export const DistributionSection = () => {
  const { request } = useStudioAuth();
  const [relay, setRelay] = useState(null);
  const [logs, setLogs] = useState([]);
  const [sourceUrl, setSourceUrl] = useState("");
  const [keys, setKeys] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [statusResponse, logResponse] = await Promise.all([request("/operations/relay"), request("/operations/relay/logs")]);
    if (statusResponse.ok) {
      const data = await statusResponse.json();
      setRelay(data);
      setSourceUrl((current) => (current ? current : data.source_url || ""));
    }
    if (logResponse.ok) setLogs(await logResponse.json());
  }, [request]);

  useEffect(() => { load(); const timer = setInterval(load, 8000); return () => clearInterval(timer); }, [load]);

  const patch = async (body, message) => {
    const response = await request("/operations/relay/settings", { method: "POST", body: JSON.stringify(body) });
    if (!response.ok) return toast.error("Setting could not be saved");
    setRelay(await response.json());
    if (message) toast.success(message);
  };

  const control = async (action) => {
    setBusy(true);
    const response = await request(`/operations/relay/${action}`, { method: "POST" });
    setBusy(false);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const blockers = data.detail?.blockers;
      return toast.error(blockers ? blockers[0] : data.detail?.message || data.detail || "Relay command failed");
    }
    setRelay(data);
    toast.success(action === "stop" ? "Relay stopped" : "Relay started");
    load();
  };

  if (!relay) return <SkeletonRows rows={6} />;

  return (
    <div className="grid gap-3 xl:grid-cols-[1fr_340px]">
      <div className="space-y-3">
        <Panel
          title="Destinations"
          icon={RadioTower}
          note={`${relay.destinations.filter((item) => item.running).length} live · ${relay.destinations.filter((item) => item.configured).length} ready`}
          actions={
            <>
              <StatusChip state={relay.running ? "live" : relay.blockers.length ? "blocked" : "idle"} testId="relay-running-status">
                {relay.running ? "On air" : relay.blockers.length ? "Blocked" : "Standby"}
              </StatusChip>
              <button data-testid="relay-refresh-button" onClick={load} title="Refresh" className="grid h-7 w-7 place-items-center border border-white/10 text-slate-400 transition-colors hover:bg-white/5 hover:text-white">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </>
          }
          bodyClass="p-0"
        >
          <div className="divide-y divide-[rgba(120,170,255,0.12)]">
            {relay.destinations.map((destination) => {
              const Icon = icons[destination.key] || RadioTower;
              return (
                <div key={destination.key} data-testid={`broadcast-destination-${destination.key}`} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="grid h-8 w-8 shrink-0 place-items-center border border-white/10 text-slate-300"><Icon className="h-4 w-4" /></div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-bold text-white">{destination.name}</div>
                        <div className="mono text-[9px] uppercase tracking-widest text-slate-600">
                          {destination.running ? `Live · ${destination.restarts} restarts` : destination.configured ? "Key saved" : "Credentials required"}
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <StatusChip state={destination.running ? "live" : destination.configured ? "ok" : "blocked"} dot={false}>
                        {destination.running ? "Live" : destination.configured ? "Ready" : "No key"}
                      </StatusChip>
                      <Switch
                        data-testid={`relay-enable-${destination.key}`}
                        checked={destination.enabled}
                        onCheckedChange={(checked) => patch({ destinations: { [destination.key]: { enabled: checked } } }, checked ? `${destination.name} enabled` : `${destination.name} disabled`)}
                      />
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {destination.key === "tiktok" && (
                      <Input
                        data-testid="relay-tiktok-server-input"
                        defaultValue={destination.server_url}
                        onBlur={(event) => patch({ destinations: { tiktok: { server_url: event.target.value } } }, "TikTok server saved")}
                        placeholder="rtmp://server-from-tiktok/live"
                        className={`${OPS_INPUT} mono min-w-[200px] flex-1 text-xs`}
                      />
                    )}
                    <Input
                      data-testid={`relay-key-${destination.key}`}
                      value={keys[destination.key] ?? ""}
                      onChange={(event) => setKeys({ ...keys, [destination.key]: event.target.value })}
                      placeholder={destination.key_hint ? `Saved ${destination.key_hint} — paste to replace` : "Paste stream key"}
                      type="password"
                      className={`${OPS_INPUT} mono min-w-[200px] flex-1 text-xs`}
                    />
                    <Button
                      data-testid={`relay-save-key-${destination.key}`}
                      onClick={() => { patch({ destinations: { [destination.key]: { stream_key: keys[destination.key] || "" } } }, `${destination.name} key saved`); setKeys({ ...keys, [destination.key]: "" }); }}
                      disabled={!keys[destination.key]}
                      className="h-9 shrink-0 rounded-none bg-[#1a5cff] px-3 text-[10px] font-bold uppercase tracking-widest hover:bg-[#2a6cff]"
                    >
                      Save key
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel title="Encoder Log" icon={AlertTriangle} note="Live FFmpeg output" bodyClass="max-h-56 overflow-y-auto p-0">
          {logs.length === 0 ? (
            <p className="mono p-4 text-[10px] uppercase tracking-widest text-slate-600">No encoder output yet.</p>
          ) : (
            <ul data-testid="relay-log-list" className="mono divide-y divide-white/5 text-[10px] text-slate-400">
              {logs.map((entry, index) => (
                <li key={`${entry.at}-${index}`} className="flex gap-2 px-3 py-1.5">
                  <span className="shrink-0 text-slate-600">{new Date(entry.at).toLocaleTimeString()}</span>
                  <span className="shrink-0 uppercase text-sky-400">{entry.destination}</span>
                  <span className="min-w-0 break-all">{entry.line}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="space-y-3">
        <Panel title="Relay Source" icon={Play} bodyClass="space-y-3 p-4">
          <div className="space-y-1.5">
            <FieldLabel>Source mode</FieldLabel>
            {modes.map((mode) => (
              <button
                key={mode.key}
                type="button"
                data-testid={`relay-mode-${mode.key}`}
                onClick={() => patch({ source_mode: mode.key }, `Source mode: ${mode.label}`)}
                className={`block w-full border px-3 py-2 text-left transition-colors ${relay.source_mode === mode.key ? "border-sky-500 bg-sky-500/10" : "border-white/10 hover:bg-white/[0.04]"}`}
              >
                <div className="text-xs font-bold uppercase text-white">{mode.label}</div>
                <div className="mono mt-0.5 text-[9px] uppercase tracking-wider text-slate-600">{mode.hint}</div>
              </button>
            ))}
          </div>
          {relay.source_mode === "url" && (
            <div className="space-y-1">
              <FieldLabel>Source stream URL</FieldLabel>
              <Input data-testid="relay-source-url-input" value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)} placeholder="https://…/playlist.m3u8" className={`${OPS_INPUT} mono text-xs`} />
              <Button data-testid="relay-save-source-button" onClick={() => patch({ source_url: sourceUrl }, "Source saved")} className="h-9 w-full rounded-none bg-white/[0.06] text-[10px] font-bold uppercase tracking-widest text-white hover:bg-white/10">
                Save source
              </Button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 border-t border-[rgba(120,170,255,0.12)] pt-3">
            <Button data-testid="relay-start-button" onClick={() => control("start")} disabled={busy || relay.running} className="h-10 rounded-none bg-red-600 text-[10px] font-bold uppercase tracking-widest hover:bg-red-500 disabled:bg-white/[0.04] disabled:text-slate-600">
              <Play className="h-4 w-4" /> Go live
            </Button>
            <Button data-testid="relay-stop-button" onClick={() => control("stop")} disabled={busy || !relay.running} variant="outline" className="h-10 rounded-none border-white/15 bg-white/[0.03] text-[10px] font-bold uppercase tracking-widest text-slate-100 hover:bg-white/[0.08]">
              <Square className="h-4 w-4" /> Stop
            </Button>
          </div>
          <Button data-testid="restart-relay-button" onClick={() => control("restart")} disabled={busy} variant="outline" className="h-9 w-full rounded-none border-white/15 bg-white/[0.03] text-[10px] font-bold uppercase tracking-widest text-slate-100 hover:bg-white/[0.08]">
            Restart relay
          </Button>
        </Panel>

        <Panel title="Watchdog" icon={AlertTriangle} bodyClass="p-4">
          <dl className="space-y-2 text-xs">
            {[
              ["State", relay.watchdog, relay.running ? "text-emerald-400" : "text-amber-300"],
              ["Media runtime", relay.runtime_available ? "Available" : "Unavailable", relay.runtime_available ? "text-emerald-400" : "text-slate-500"],
              ["Source", relay.source_configured ? "Configured" : "Not set", relay.source_configured ? "text-emerald-400" : "text-slate-500"],
            ].map(([label, value, tone]) => (
              <div key={label} className="flex items-center justify-between gap-3">
                <dt className="text-slate-400">{label}</dt>
                <dd data-testid={`relay-watchdog-${label.toLowerCase().replace(/\s+/g, "-")}`} className={`mono text-[10px] font-bold uppercase tracking-widest ${tone}`}>{value}</dd>
              </div>
            ))}
          </dl>
          {relay.blockers.length > 0 && (
            <div data-testid="relay-blockers" className="mt-3 border border-amber-400/25 bg-amber-400/[0.06] p-3">
              <div className="mono flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-200"><AlertTriangle className="h-3.5 w-3.5" /> Startup blockers</div>
              <ul className="mt-2 space-y-1.5 text-[11px] text-slate-300">
                {relay.blockers.map((blocker) => <li key={blocker} className="flex gap-2"><span className="text-amber-400">—</span>{blocker}</li>)}
              </ul>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default DistributionSection;
